import { PAGE_URLS } from "@constants/common";
import { useVerifyEmailMutation } from "@features/auth/authApi";
import LoginForm from "@features/auth/loginForm/LoginForm";
import PasswordLogin from "@features/auth/passwordLogin/PasswordLogin";
import VerifyOtp from "@features/auth/verifyOtp/VerifyOtp";
import PageLoader from "@features/common/pageLoader/PageLoader";
import { useAppSelector } from "@redux/hooks";
import * as cookie from "cookie";
import { checkTokenExpired } from "@utils/common";
import { useState, useEffect, useCallback, FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";


const LoginPage: FC = () => {
  // ============================================================================
  // State Management
  // ============================================================================

  /** Controls visibility of password login screen */
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);

  /** Controls visibility of OTP verification screen */
  const [showOtpVerification, setShowOtpVerification] = useState(false);

  /** Stores the verified/entered email address across login steps */
  const [email, setEmail] = useState("");

  /** OTP-related action URLs from login response */
  const [otpUrls, setOtpUrls] = useState(null);

  // ============================================================================
  // Hooks & API
  // ============================================================================

  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAppSelector((state) => state.auth);

  /** API mutation for verifying email (used for auto-login flow) */
  const [verifyEmail, { data, error, isLoading, isSuccess }] =
    useVerifyEmailMutation();

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Removes query parameters from URL without triggering navigation
   * Used after processing auto-login params
   */
  const clearUrlQueryParams = useCallback(() => {
    const newUrl = `${location.pathname}${location.hash || ""}`;
    window.history.replaceState({}, "", newUrl);
  }, [location.pathname, location.hash]);

  /**
   * Resets login flow to initial state (email entry screen)
   * Clears URL params and hides password/OTP screens
   */
  const handleToggleToLoginForm = () => {
    clearUrlQueryParams();
    setShowPasswordLogin(false);
    setShowOtpVerification(false);
  };

  /**
   * Transitions from password login to OTP verification
   * Called after successful password authentication when 2FA is enabled
   */
  const handlePasswordLoginSuccess = () => {
    setShowOtpVerification(true);
  };

  /**
   * Callback when email is successfully verified
   * Stores email and transitions to password entry screen
   */
  const onEmailVerified = (verifiedEmail: string) => {
    setEmail(verifiedEmail);
    setShowPasswordLogin(true);
  };

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Guard: Redirect already-authenticated users to corporate selection
   * Only redirects when on the initial login form (not mid-flow)
   */
  useEffect(() => {
    const { access_token_expiry } = cookie.parse(document.cookie);
    const isTokenValid =
      access_token_expiry && !checkTokenExpired(access_token_expiry);
    const isOnInitialLoginForm = !showPasswordLogin && !showOtpVerification;

    if (isTokenValid && isOnInitialLoginForm) {
      navigate(PAGE_URLS.SELECT_CORPORATE, { replace: true });
    }
  }, [accessToken, navigate, showPasswordLogin, showOtpVerification]);

  /**
   * Auto-show password login if URL contains query params
   * Handles cases like direct links or deep linking
   */
  useEffect(() => {
    const hasQueryParams = Boolean(
      location.search && location.search.length > 0,
    );
    setShowPasswordLogin(hasQueryParams);
  }, [location.search]);

  /**
   * Auto-login Flow: Handle encrypted `r` parameter
   * When `r` param exists, automatically verify email for passwordless login
   */

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasAutoLoginParam = params.has("r");
    setShowPasswordLogin(hasAutoLoginParam);
  }, [location.search]);

  useEffect(() => {
    if (!location.search) return;

    const params = new URLSearchParams(location.search);
    const encryptedKey = params.get("r");

    if (!encryptedKey) return;

    try {
      verifyEmail({
        username: encryptedKey,
        is_encrypted: true,
      });
    } catch (err) {
      console.error("Auto-login verification failed:", err);
    }
  }, [location.search, verifyEmail]);

  /**
   * Handle auto-login email verification response
   * On success: store email, show password screen, clean URL
   * On error: reset to login form
   */
  useEffect(() => {
    if (isSuccess) {
      const username = data?.data?.username;
      if (username) {
        setEmail(username);
      }
      setShowPasswordLogin(true);
      clearUrlQueryParams();
    }

    if (error) {
      handleToggleToLoginForm();
    }
  }, [isSuccess, data, error, clearUrlQueryParams]);

  // ============================================================================
  // Render
  // ============================================================================

  /**
   * Render appropriate screen based on login flow state:
   * 1. OTP Verification (if 2FA enabled after password login)
   * 2. Password Login (after email verification)
   * 3. Email Entry (initial state)
   */
  return (
    <>
      {showOtpVerification ? (
        // Step 3: Two-Factor Authentication
        <VerifyOtp
          isForgotPassword={false}
          otpUrls={otpUrls}
          verifiedEmail={email}
        />
      ) : showPasswordLogin ? (
        // Step 2: Password Entry (with loading state for auto-login)
        isLoading ? (
          <PageLoader />
        ) : (
          <PasswordLogin
            onToggleToLoginForm={handleToggleToLoginForm}
            onPasswordLoginSuccess={handlePasswordLoginSuccess}
            verifiedEmail={email}
            setOtpUrls={setOtpUrls}
          />
        )
      ) : (
        // Step 1: Email Verification
        <LoginForm
          onEmailVerified={onEmailVerified}
          initialEmail={email}
          onEmailChange={setEmail}
        />
      )}
    </>
  );
};

export default LoginPage;
